/**
 * AccountBackupSection — Settings-page card that lets a user download a
 * ZIP archive of everything they've made on WrappedInMusic.
 *
 * Backend contract:
 *   GET /api/account/backup/status → { cost_credits, cooldown_hours,
 *     cooldown_seconds_remaining, user_credits, last_backup, content_summary }
 *   GET /api/account/backup        → streams a .zip file (charges credits)
 *
 * The download is triggered client-side by pointing an <a> at the endpoint
 * with a signed auth cookie — the browser handles the file save natively,
 * so we don't need to buffer the ZIP in JS memory.
 */

import { useCallback, useEffect, useState } from 'react';
import { Download, Archive, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { API, apiFetch } from '../App';
import { toast } from 'sonner';

const fmtBytes = (b) => {
  if (!b) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1073741824) return `${(b / 1048576).toFixed(1)} MB`;
  return `${(b / 1073741824).toFixed(2)} GB`;
};

export default function AccountBackupSection() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiFetch('/account/backup/status');
      setStatus(data);
    } catch (err) {
      // Non-blocking — user can still trigger a backup; the endpoint will
      // enforce cost / cooldown server-side. We just can't preview.
      // eslint-disable-next-line no-console
      console.warn('backup status fetch failed', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const startBackup = async () => {
    if (!status) return;
    if (status.user_credits < status.cost_credits) {
      toast.error(`Backup costs ${status.cost_credits} credits — you have ${status.user_credits}.`);
      return;
    }
    if (!window.confirm(
      `Download a ZIP archive of your entire account?\n\n`
      + `• ${status.content_summary?.songs || 0} songs (MP3 + embedded cover)\n`
      + `• ${status.content_summary?.covers || 0} album covers\n`
      + `• ${status.content_summary?.ai_clips || 0} AI clips\n`
      + `• ${status.content_summary?.playlists || 0} playlists\n`
      + `• ${status.content_summary?.purchases || 0} purchases (billing.csv)\n`
      + `• Credit history + manifest\n\n`
      + `Cost: ${status.cost_credits} credits. Run as often as you like.`
    )) return;

    setDownloading(true);
    try {
      // Use native fetch → blob → object URL so we can honor error responses
      // (429 cooldown, 402 insufficient credits) and show a real toast.
      // For huge accounts this holds the ZIP in memory; a follow-up could
      // stream direct to disk via a signed URL once backups exceed ~500MB.
      const token = window.localStorage.getItem('auth_token');
      const res = await fetch(`${API}/account/backup`, {
        method: 'GET',
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        let msg = `Backup failed (${res.status})`;
        try {
          const err = await res.json();
          if (err?.detail) msg = err.detail;
        } catch { /* body wasn't JSON */ }
        throw new Error(msg);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Grab the filename the server suggested if present, otherwise fall back
      const disp = res.headers.get('Content-Disposition') || '';
      const match = disp.match(/filename="([^"]+)"/);
      a.download = match ? match[1] : `wrappedinmusic-backup.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Backup ready — check your Downloads folder.');
      await refresh();
    } catch (err) {
      toast.error(err.message || 'Backup failed');
    } finally {
      setDownloading(false);
    }
  };

  const cost = status?.cost_credits ?? 10;
  const balance = status?.user_credits ?? 0;
  const canAfford = balance >= cost;
  const disabled = loading || downloading || !canAfford;

  return (
    <div className="glass-card p-6" data-testid="account-backup-section">
      <h2 className="text-xl font-semibold text-white mb-1 flex items-center gap-2">
        <Archive className="h-5 w-5 text-emerald-400" />
        Download your account
      </h2>
      <p className="text-sm text-zinc-400 mb-4">
        Bundle every song, cover, AI clip, playlist, purchase receipt, and credit-history record
        into a single ZIP archive. Personal-use license — see the included <span className="font-mono text-xs">README.txt</span>.
      </p>

      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
        <div className="bg-black/30 rounded p-3 border border-white/5">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">Songs</div>
          <div className="text-lg font-semibold text-white" data-testid="account-backup-song-count">
            {status?.content_summary?.songs ?? '—'}
          </div>
        </div>
        <div className="bg-black/30 rounded p-3 border border-white/5">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">Covers</div>
          <div className="text-lg font-semibold text-white">{status?.content_summary?.covers ?? '—'}</div>
        </div>
        <div className="bg-black/30 rounded p-3 border border-white/5">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">AI Clips</div>
          <div className="text-lg font-semibold text-white">{status?.content_summary?.ai_clips ?? '—'}</div>
        </div>
        <div className="bg-black/30 rounded p-3 border border-white/5">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">Playlists</div>
          <div className="text-lg font-semibold text-white" data-testid="account-backup-playlist-count">
            {status?.content_summary?.playlists ?? '—'}
          </div>
        </div>
        <div className="bg-black/30 rounded p-3 border border-white/5">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">Purchases</div>
          <div className="text-lg font-semibold text-white" data-testid="account-backup-purchase-count">
            {status?.content_summary?.purchases ?? '—'}
          </div>
        </div>
        <div className="bg-black/30 rounded p-3 border border-white/5">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">Cost</div>
          <div className="text-lg font-semibold text-emerald-400" data-testid="account-backup-cost">
            {cost} credits
          </div>
        </div>
      </div>

      {!canAfford && (
        <div className="bg-rose-500/10 border border-rose-500/30 rounded p-3 mb-4 text-sm text-rose-200">
          You need {cost} credits — your balance is {balance}. Top up from the Credits page and come back.
        </div>
      )}

      {status?.last_backup && (
        <div className="text-xs text-zinc-500 mb-4">
          Last backup:{' '}
          {new Date(status.last_backup.created_at).toLocaleString()}
          {status.last_backup.size_bytes ? ` · ${fmtBytes(status.last_backup.size_bytes)}` : ''}
        </div>
      )}

      <Button
        onClick={startBackup}
        disabled={disabled}
        className="btn-primary"
        data-testid="account-backup-download-btn"
      >
        {downloading ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Building archive… (this can take a minute)
          </>
        ) : (
          <>
            <Download className="h-4 w-4 mr-2" />
            Download my account ({cost} credits)
          </>
        )}
      </Button>
    </div>
  );
}
